import { memo, useEffect } from "react";
import styles from "./AppliedFilter.module.scss";
import { EMPTY_LOCATION, INITIAL_FILTERS, type appliedFilter, type ClearFilterKey, type showFilters } from "./FilterInterface";
import CloseIcon from "@mui/icons-material/Close";
import { DATE_FILTER_CONFIG, FILTER_CONFIG, GEOFENCE_FILTER_CONFIG, SPECIAL_SELECT_CONFIG, TEXT_FILTER_CONFIG } from "./FilterConfig";
import FilterChip from "./FilterChip";
import DateFilterChip from "./DateFilterChip";
import LocationFilterChip from "./LocationFilterChip";
import { dateFormatter } from "@/components/DateFormat/DateFormat";

interface AppliedFilterProps {
    filterData: appliedFilter;
    onClearFilter: (updated: appliedFilter) => void;
    defaultDateType?: string;
    defaultDateLabel?: string;
    showFilters?: showFilters;
    filterName?: string;
}

const AppliedFilter: React.FC<AppliedFilterProps> = ({ filterData, onClearFilter, defaultDateType, defaultDateLabel, showFilters, filterName }) => {
    const closeIcon = <CloseIcon fontSize="small" />;

    const ALL_FILTER_CONFIG = [
        ...Object.values(FILTER_CONFIG),
        ...Object.values(SPECIAL_SELECT_CONFIG),
        ...GEOFENCE_FILTER_CONFIG,
        ...Object.values(TEXT_FILTER_CONFIG),
        ...Object.values(DATE_FILTER_CONFIG),
    ];

    const getIsMulti = (filterKey: keyof appliedFilter) => {
        const config = ALL_FILTER_CONFIG.find(
            (c) => c.filterKey === filterKey
        );

        return config ? isMultiFilter(config.showFilterKey) : false;
    };

    const isMultiFilter = (key: keyof showFilters) => {
        return showFilters?.[key as keyof Omit<showFilters, "location">]?.mode === "multi";
    };

    const clearFilter = (
        name: ClearFilterKey,
        value?: string,
        index?: number
    ) => {
        const updated: appliedFilter = {
            ...filterData,
            location: {
                ...EMPTY_LOCATION,
                ...(filterData.location ?? {}),
            },
        };

        if (name === "date") {
            updated.fromDate = null;
            updated.toDate = null;
            updated.dateType = defaultDateType ?? "";
        } else if (name === "year") {
            updated.fromYear = null;
            updated.toYear = null;
        } else if (name === "location") {
            const location = updated.location!;

            if (value === "locality") {
                location.locality = null;
            } else if (value === "city") {
                location.city = null;
                location.locality = null;
            } else if (value === "state") {
                location.state = null;
                location.city = null;
                location.locality = null;
            } else {
                updated.location = { ...EMPTY_LOCATION };
            }
        } else if (name === "settingType") {
            updated.settingType = null;
            updated.settingStatus = null;
        } else {
            const key = name as keyof appliedFilter;
            const isMulti = getIsMulti(key);

            updated[key] = isMulti
                ? (index !== undefined
                    ? (updated[key] as any[])?.filter((_, i) => i !== index)
                    : [])
                : INITIAL_FILTERS[key];
        }

        onClearFilter(updated);
    };

    const clearAllFilters = () => {
        onClearFilter({
            ...INITIAL_FILTERS,
            ...(defaultDateType && { dateType: defaultDateType }),
        });
    };

    const hasDate = Boolean(
        filterData.fromDate ||
        filterData.toDate ||
        filterData.fromYear ||
        filterData.toYear
    );

    const hasValue = (value: any, isMulti: boolean) => {
        if (isMulti) {
            return Array.isArray(value) && value.length > 0;
        }

        if (value == null) return false;

        if (typeof value === "string") {
            return value.trim() !== "";
        }

        return true;
    };

    const hasConfigFilters = ALL_FILTER_CONFIG.some((config) => {
        const visible = showFilters?.[
            config.showFilterKey as keyof Omit<showFilters, "location">
        ]?.show;

        return (
            visible &&
            hasValue(
                filterData[config.filterKey],
                isMultiFilter(config.showFilterKey)
            )
        );
    });

    const hasLocation = Object.values(filterData.location ?? {}).some(Boolean);
    const hasSettingFilter = Boolean(showFilters?.settingType?.show && filterData?.settingType);
    const hasAnyFilter = hasDate || hasLocation || hasConfigFilters || hasSettingFilter;

    const dateTypeLabel = typeof filterData.dateType === "object"
        ? filterData.dateType?.label
        : filterData.dateType;

    return (
        <div className="flex flex-wrap gap-4">
            {/* Date Filter Chip */}
            {showFilters?.date?.show && (
                <DateFilterChip
                    title={defaultDateLabel ?? dateTypeLabel ?? defaultDateType}
                    fromValue={filterData.fromDate}
                    toValue={filterData.toDate}
                    format="dd MMM yyyy"
                    filterKey="date"
                    clearFilter={clearFilter}
                />
            )}

            {/* Year Filter Chip */}
            {showFilters?.year?.show && (
                <DateFilterChip
                    title="Year"
                    fromValue={filterData.fromYear}
                    toValue={filterData.toYear}
                    format="yyyy"
                    filterKey="year"
                    clearFilter={clearFilter}
                />
            )}

            {/* Location Filter Chip */}
            {showFilters?.location && (
                <LocationFilterChip
                    location={filterData.location}
                    clearFilter={clearFilter}
                />
            )}

            {/* Multiple/single Selection Chip */}
            {Object.values(FILTER_CONFIG)
                .filter((config) => showFilters?.[config.showFilterKey as keyof Omit<showFilters, "location">]?.show)
                .map((config) => {
                    const isMulti = isMultiFilter(config.showFilterKey);

                    return (
                        <FilterChip
                            key={String(config.filterKey)}
                            title={config.getTitle?.(filterName) || config.title}
                            idKey={config.idKey}
                            nameKey={config.nameKey}
                            filterKey={config.filterKey}
                            clearFilter={clearFilter}
                            {...(isMulti
                                ? { multiValues: filterData?.[config.filterKey] ?? [] }
                                : { singleValues: filterData?.[config.filterKey] ?? "" })}
                        />
                    );
                })}

            {Object.values(SPECIAL_SELECT_CONFIG)
                .filter((config) => showFilters?.[config.showFilterKey as keyof Omit<showFilters, "location">]?.show)
                .map((config) => {
                    const isMulti = isMultiFilter(config.showFilterKey);

                    return (
                        <FilterChip
                            key={String(config.filterKey)}
                            title={config.title}
                            idKey={config.idKey}
                            nameKey={config.nameKey}
                            filterKey={config.filterKey}
                            clearFilter={clearFilter}
                            {...(isMulti
                                ? { multiValues: filterData?.[config.filterKey] ?? [] }
                                : { singleValues: filterData?.[config.filterKey] ?? "" })}
                        />
                    );
                })}

            {Object.values(GEOFENCE_FILTER_CONFIG)
                .filter((config) => showFilters?.[config.showFilterKey as keyof Omit<showFilters, "location">]?.show)
                .map((config) => {
                    const isMulti = isMultiFilter(config.showFilterKey);

                    return (
                        <FilterChip
                            key={String(config.filterKey)}
                            title={config.title}
                            idKey={config.idKey}
                            nameKey={config.nameKey}
                            filterKey={config.filterKey}
                            {...(isMulti
                                ? { multiValues: filterData?.[config.filterKey] ?? [] }
                                : { singleValues: filterData?.[config.filterKey] ?? null })}
                            clearFilter={clearFilter}
                        />
                    );
                })}

            {/* Text Filter Chip */}
            {Object.values(TEXT_FILTER_CONFIG)
                .filter((config) => showFilters?.[config.showFilterKey as keyof Omit<showFilters, "location">]?.show)
                .map((config) => (
                    <FilterChip
                        key={String(config.filterKey)}
                        title={config.title}
                        singleValues={filterData?.[config.filterKey] ?? ""}
                        filterKey={config.filterKey}
                        clearFilter={clearFilter}
                    />
                ))}

            {/* Date Filter Chip */}
            {Object.values(DATE_FILTER_CONFIG)
                .filter((config) => showFilters?.[config.showFilterKey as keyof Omit<showFilters, "location">]?.show)
                .map((config) => (
                    <FilterChip
                        key={String(config.filterKey)}
                        title={config.title}
                        singleValues={
                            filterData?.[config.filterKey]
                                ? dateFormatter(filterData[config.filterKey], "dd MMM yyyy")
                                : ""
                        }
                        filterKey={config.filterKey}
                        clearFilter={clearFilter}
                    />
                ))}

            {/* Configuration Status Filter Chip */}
            {showFilters?.settingType?.show && filterData?.settingType && (
                <FilterChip
                    title="By Configuration Status"
                    singleValues={`${filterData.settingType.label} - ${filterData.settingStatus?.label ?? "Enable"}`}
                    filterKey="settingType"
                    clearFilter={clearFilter}
                />
            )}

            {/* clear all filter */}
            {hasAnyFilter && (
                <button type="button" className={`${styles.customClose} text-[#74788d]`} onClick={clearAllFilters}>
                    {closeIcon}
                    <span>Clear Filter</span>
                </button>
            )}
        </div>
    );
};

export default memo(AppliedFilter);
